// Encaminhamentos do usuário (na tela "Minhas tarefas"): o que ele precisa
// resolver (foi marcado) e o que ele abriu e acompanha. Cada um é um fio de
// discussão com histórico.
import { useEffect, useMemo, useState } from 'react';
import { computeProductSummary } from '../shared/criticalPath';
import { buildTemplateForType } from '../shared/terceirizacao';
import { loadMyEncaminhamentos, type Discussion } from '../lib/encaminhamentos';
import { DiscussionThread } from './DiscussionThread';
import type { Product, StageTemplate } from '../shared/types';

export function EncaminhamentosPanel({
  userId,
  fullName,
  isAdmin,
  products,
  template,
}: {
  userId: string | null;
  fullName: string | null;
  isAdmin: boolean;
  products: Product[];
  template: StageTemplate[];
}) {
  const [encs, setEncs] = useState<Discussion[]>([]);
  const reload = () => {
    if (userId) loadMyEncaminhamentos(userId).then(setEncs);
  };
  useEffect(reload, [userId]);

  const anchorNFor = useMemo(() => {
    return (productId: string | null): number | undefined => {
      if (!productId) return undefined;
      const p = products.find((x) => x.id === productId);
      if (!p) return 1;
      const tpl = buildTemplateForType(template, p.projectType ?? 'interno');
      const sum = computeProductSummary(p, tpl, new Date());
      return (
        sum.proximaEtapa?.n ??
        sum.stages.find((c) => c.status !== 'concluido')?.n ??
        sum.stages[0]?.n ??
        1
      );
    };
  }, [products, template]);

  const souParticipante = (e: Discussion) =>
    e.assignedToId === userId || (userId != null && e.participantIds.includes(userId));
  const paraResolver = encs.filter(souParticipante);
  const abertosPorMim = encs.filter((e) => !souParticipante(e) && e.createdById === userId);

  if (!paraResolver.length && !abertosPorMim.length) return null;

  const row = (e: Discussion) => (
    <DiscussionThread
      key={e.id}
      d={e}
      userId={userId}
      fullName={fullName}
      isAdmin={isAdmin}
      anchorN={anchorNFor(e.productId)}
      onChanged={reload}
    />
  );

  return (
    <div className="card card-pad" style={{ marginBottom: 14 }}>
      <div className="section-title">
        <h2>
          Encaminhamentos <span className="muted">({paraResolver.length + abertosPorMim.length})</span>
        </h2>
        <span className="muted" style={{ fontSize: 12 }}>discussões que saíram das reuniões</span>
      </div>

      {paraResolver.length > 0 && (
        <>
          <h3 className="enc-h">Para você resolver</h3>
          {paraResolver.map(row)}
        </>
      )}
      {abertosPorMim.length > 0 && (
        <>
          <h3 className="enc-h">Abertos por você (acompanhe e encerre)</h3>
          {abertosPorMim.map(row)}
        </>
      )}
    </div>
  );
}
